import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { LAYER_ID_ATTR, type DismissVerdict, type LayerStack, findTwin, getLayerStack } from './stack';
import type { LayerCloseReason, LayerOptions } from './types';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const StackContext = createContext<LayerStack | null>(null);
/** Id of the layer whose content is being rendered (layers opened inside it are its children). */
const ParentContext = createContext<string | null>(null);

/** Provide a stack (LayerRoot, tests). Without a provider the document's singleton is used. */
export function LayerStackProvider({ stack, children }: { stack: LayerStack; children: ReactNode }) {
  return <StackContext.Provider value={stack}>{children}</StackContext.Provider>;
}

export function useLayerStack(): LayerStack {
  return useContext(StackContext) ?? getLayerStack();
}

/** Wrap a layer's content so layers opened inside it become its children (suspend/close rules). */
export function LayerParent({ id, children }: { id: string; children: ReactNode }) {
  return <ParentContext.Provider value={id}>{children}</ParentContext.Provider>;
}

export function useParentLayerId(): string | null {
  return useContext(ParentContext);
}

export type LayerHandle = {
  id: string;
  /** Portal container (`#dr-layers`). */
  container: HTMLElement | null;
  /** Spread on the layer's portal element: DOM order inside the container = stacking order. */
  portalProps: Record<typeof LAYER_ID_ATTR, string>;
  /** Whether this layer is the top-most open layer. */
  isTop: boolean;
  /** Ask the stack to close (dirty guard for guarded reasons). */
  requestClose: (reason: LayerCloseReason) => Promise<boolean>;
  /** Base UI `onOpenChange` adapter: decide whether a dismiss detected by the primitive applies. */
  dismiss: (reason: LayerCloseReason, event?: Event) => DismissVerdict;
  /**
   * Where focus returns on close (Base UI `finalFocus`): the element focused when the layer opened,
   * or `true` (the primitive's default) when it is gone; `false` (stay) when focus already moved on
   * to something outside the layer (see `focusMovedAway`).
   */
  finalFocus: () => HTMLElement | boolean;
  /**
   * True while focus rests on a connected element outside this layer: the member clicked or tabbed
   * into another field, or a layer above took focus. A closing layer then leaves focus where it is
   * instead of pulling it back to its trigger.
   */
  focusMovedAway: () => boolean;
};

/**
 * Register a layer while `open`. Primitives call this; feature code calls it only for hand-made
 * floating surfaces (a caret menu inside an editor) so Escape and outside presses route correctly.
 */
export function useLayer(options: LayerOptions): LayerHandle {
  const stack = useLayerStack();
  const parentId = useParentLayerId();
  const reactId = useId();
  const id = useMemo(() => `l${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`, [reactId]);
  const optsRef = useRef(options);
  optsRef.current = options;
  // The element focused when the layer opens (its trigger), captured before the primitive moves focus.
  const wasOpen = useRef(false);
  const opener = useRef<Element | null>(null);
  const openerChain = useRef<Element[]>([]);
  if (options.open && !wasOpen.current && typeof document !== 'undefined') {
    opener.current = document.activeElement;
    openerChain.current = [];
    for (let el = opener.current?.parentElement ?? null; el; el = el.parentElement)
      openerChain.current.push(el);
  }
  wasOpen.current = options.open;

  const { open } = options;
  useIsoLayoutEffect(() => {
    if (!open) return;
    stack.register(id, optsRef, { parentId, opener: opener.current });
    return () => stack.unregister(id);
  }, [open, stack, id, parentId]);

  // An unmounted layer is closed for good (the stack never re-registers it).
  useEffect(
    () => () => {
      optsRef.current = { ...optsRef.current, open: false };
    },
    [],
  );

  const dirty = Boolean(options.dirty);
  useIsoLayoutEffect(() => {
    if (open) stack.touch();
  }, [dirty, open, stack]);

  const subscribe = useCallback((l: () => void) => stack.subscribe(l), [stack]);
  const isTop = useSyncExternalStore(
    subscribe,
    () => stack.isTop(id),
    () => false,
  );
  const requestClose = useCallback((reason: LayerCloseReason) => stack.requestClose(id, reason), [stack, id]);
  const dismiss = useCallback(
    (reason: LayerCloseReason, event?: Event) => stack.handleDismiss(id, reason, event),
    [stack, id],
  );
  const focusMovedAway = useCallback((): boolean => {
    if (typeof document === 'undefined') return false;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active === document.body || !active.isConnected) return false;
    if (active === opener.current) return false;
    const own = document.querySelector(`[${LAYER_ID_ATTR}="${id}"]`);
    return !own?.contains(active);
  }, [id]);
  const finalFocus = useCallback((): HTMLElement | boolean => {
    if (focusMovedAway()) return false;
    const el = opener.current;
    if (!(el instanceof HTMLElement) || el === document.body) return true;
    if (el.isConnected) return el;
    // The trigger re-rendered while the layer was open (a filter chip): the same control in its place.
    const twin = findTwin(el, openerChain.current);
    return twin instanceof HTMLElement ? twin : true;
  }, [focusMovedAway]);
  return {
    id,
    finalFocus,
    focusMovedAway,
    container: stack.container('layers'),
    portalProps: { [LAYER_ID_ATTR]: id } as Record<typeof LAYER_ID_ATTR, string>,
    isTop,
    requestClose,
    dismiss,
  };
}

/** Subscribe to the stack (hosts, dev tools, tests). */
export function useLayerEntries() {
  const stack = useLayerStack();
  const subscribe = useCallback((l: () => void) => stack.subscribe(l), [stack]);
  const cache = useRef<{ key: string; value: ReturnType<LayerStack['list']> }>({ key: '', value: [] });
  return useSyncExternalStore(
    subscribe,
    () => {
      const value = stack.list();
      const key = JSON.stringify(value);
      if (key !== cache.current.key) cache.current = { key, value };
      return cache.current.value;
    },
    () => cache.current.value,
  );
}
